import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, query, onSnapshot, updateDoc, addDoc, deleteDoc, Timestamp, orderBy, getDocs } from 'firebase/firestore'; 
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';

// --- Configuration ---

const STATIC_APP_ID = "PremierCricketAcademyData";
const ACADEMY_LOGO_URL = "https://firebasestorage.googleapis.com/v0/b/premier-cricket-academy.firebasestorage.app/o/Premier%20Cricket%20Academy%20Logo.png?alt=media&token=bfc3872c-1468-4ca6-a0eb-8cea47bebcc6";

const firebaseConfig = {
  apiKey: "AIzaSyDOEW6-T1Rbh92LfDLEenT7GWIGGiIyOnk", 
  authDomain: "premier-cricket-academy.firebaseapp.com",
  projectId: "premier-cricket-academy",
  storageBucket: "premier-cricket-academy.firebasestorage.app",
  messagingSenderId: "994662224165",
  appId: "1:994662224165:web:c731604612fabe314f4acc",
  measurementId: "G-4W7RCKRG04"
};

// --- Utility Components ---

const AcademyLogoHeader = () => (
    <img
        src={ACADEMY_LOGO_URL}
        alt="Premier Cricket Academy Logo"
        className="w-8 h-8 rounded-full border-2 border-orange-500 mr-2"
        onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/32x32/0A2342/FFFFFF?text=L"; }}
    />
);

const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? `https://img.youtube.com/vi/${match[2]}/mqdefault.jpg` : null;
};

const MessageBox = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
        <h3 className="text-xl font-semibold text-red-600">Application Message</h3>
        <p className="text-gray-700">{message}</p>
        <button onClick={onClose} className="w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition duration-150">Close</button>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Please Confirm</h3>
                <p className="text-gray-700">{message}</p>
                <div className="flex justify-end space-x-3">
                    <button onClick={onCancel} className="py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition duration-150">Cancel</button>
                    <button onClick={onConfirm} className="py-2 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition duration-150">Confirm</button>
                </div>
            </div>
        </div>
    );
};

const ForgotPasswordModal = ({ isOpen, onClose, auth, onShowMessage }) => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSent, setIsSent] = useState(false);

    if (!isOpen) return null;

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setIsSent(false);
        try {
            await sendPasswordResetEmail(auth, email);
            setIsSent(true);
            onShowMessage("If an account exists for that email, a password reset link has been sent.");
        } catch (error) {
            console.error("Error sending password reset email:", error);
            if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-email') {
                setIsSent(true);
                onShowMessage("If an account exists for that email, a password reset link has been sent.");
            } else {
                 onShowMessage(`Error: ${error.message}`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">Forgot Password</h3>
                {isSent ? (
                    <div className="text-center space-y-4">
                        <p className="text-green-600 font-medium">Reset email sent!</p>
                        <p className="text-gray-700">Please check your email inbox (and spam folder) for a link to reset your password.</p>
                    </div>
                ) : (
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <p className="text-gray-600">Enter the email address associated with your account.</p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email Address</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="name@example.com" required />
                        </div>
                        <button type="submit" disabled={isLoading} className="w-full py-2 px-4 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition duration-150 disabled:opacity-50">{isLoading ? 'Sending...' : 'Send Reset Link'}</button>
                    </form>
                )}
                <button onClick={onClose} className="w-full py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition duration-150">{isSent ? 'Close' : 'Cancel'}</button>
            </div>
        </div>
    );
};

const DisabledAccountCard = ({ auth, navigate }) => {
    const handleLogout = async () => {
        await signOut(auth);
        navigate('login');
    };
    return (
        <div className="w-full max-w-md bg-white p-8 md:p-10 rounded-xl shadow-2xl text-center border-4 border-red-500">
            <h2 className="text-3xl font-bold text-red-600 mb-4">Account Disabled</h2>
            <p className="text-gray-700 mb-6">Your account has been deactivated by an administrator. Please contact the Premier Cricket Academy administration for assistance.</p>
            <button onClick={handleLogout} className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-md transition duration-150">Log Out</button>
        </div>
    );
};

const MyProfileModal = ({ isOpen, onClose, profile, db, onShowMessage }) => {
    const [firstName, setFirstName] = useState(profile?.firstName || '');
    const [lastName, setLastName] = useState(profile?.lastName || '');
    const [isSaving, setIsSaving] = useState(false);
    const appId = STATIC_APP_ID;
     
    useEffect(() => {
        if (profile) {
            setFirstName(profile.firstName || '');
            setLastName(profile.lastName || '');
        }
    }, [profile]);

    if (!isOpen) return null;

    const handleSave = async () => {
        if (!firstName || !lastName) {
            onShowMessage("First and Last name cannot be empty.");
            return;
        }
        setIsSaving(true);
        try {
            const userDocRef = doc(db, `artifacts/${appId}/public/data/profiles`, profile.userId);
            await updateDoc(userDocRef, { firstName, lastName });
            onShowMessage("Profile updated successfully!");
            onClose();
        } catch (error) {
            console.error("Error updating profile:", error);
            onShowMessage(`Error updating profile: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">My Profile</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email Address</label>
                        <p className="text-gray-600 p-2 bg-gray-100 rounded-md border border-gray-300">{profile?.email}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">First Name</label>
                        <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Last Name</label>
                        <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                </div>
                <div className="flex justify-end space-x-3">
                    <button onClick={onClose} className="py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition duration-150">Cancel</button>
                    <button onClick={handleSave} disabled={isSaving} className="py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition duration-150 disabled:opacity-50">{isSaving ? 'Saving...' : 'Save Changes'}</button>
                </div>
            </div>
        </div>
    );
};

const DashboardNavMenu = ({ onLogout, roleSwitcherComponent, tabs, activeTab, onTabChange, onShowProfile }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleTabClick = (tabId) => {
        onTabChange(tabId);
        setIsMenuOpen(false);
    };

    return (
        <div className="relative">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            {isMenuOpen && (
                <div className="absolute top-12 right-0 w-64 bg-white rounded-lg shadow-xl z-50 border border-gray-200">
                    <div className="p-4 border-b border-gray-200">{roleSwitcherComponent}</div>
                    <div className="py-2">
                        <button onClick={() => { onShowProfile(); setIsMenuOpen(false); }} className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100">My Profile</button>
                    </div>
                    <div className="border-t border-gray-200 py-2">
                        {tabs.map(tab => (
                            <button key={tab.id} onClick={() => handleTabClick(tab.id)} className={`w-full text-left px-4 py-2 ${activeTab === tab.id ? 'font-bold text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-100'}`}>{tab.label}</button>
                        ))}
                    </div>
                    <div className="border-t border-gray-200 py-2">
                        <button onClick={() => { onLogout(); setIsMenuOpen(false); }} className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50">Logout</button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Constants ---
const ALL_ROLES = ['Admin', 'Coach', 'Parent', 'Player'];
const ROLE_PRIORITY = ['Admin', 'Coach', 'Parent', 'Player']; 
const METRIC_CATEGORIES = ['Fitness', 'Batting', 'Bowling', 'Fielding', 'Wicket-Keeping', 'Mental'];
const SKILL_CATEGORIES = ['Batting', 'Bowling', 'Fielding', 'Fitness', 'Mental']; // For Radar Chart

// --- Helper Functions ---
const formatDate = (date) => {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + (d.getDate());
    const year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
};

const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const safeRoles = (user) => (Array.isArray(user?.roles) ? user.roles : []);

// --- NEW FEATURE: Session Video Archive (Replaces Resource Library) ---

const ResourceLibrary = ({ db, user, role, onShowMessage, allUsers = [] }) => {
    const appId = STATIC_APP_ID;
    const [sessionsWithVideo, setSessionsWithVideo] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);

    // Determine which players are available to select
    const availablePlayers = useMemo(() => {
        if (role === 'Player') return [user]; // Only self
        if (role === 'Parent') {
            // Children assigned to this parent
            return allUsers.filter(u => u.isActive && safeRoles(u).includes('Player') && (u.assignedParents || []).includes(user.uid));
        }
        if (role === 'Coach') {
            // Players assigned to this coach
            return allUsers.filter(u => u.isActive && safeRoles(u).includes('Player') && (u.assignedCoaches || []).includes(user.uid)).sort((a,b) => (a.lastName || '').localeCompare(b.lastName || ''));
        }
        if (role === 'Admin') {
            return allUsers.filter(u => u.isActive && safeRoles(u).includes('Player')).sort((a,b) => (a.lastName || '').localeCompare(b.lastName || ''));
        }
        return [];
    }, [allUsers, role, user]);

    // Auto-select if only one option (Player or Single Child)
    useEffect(() => {
        if (!selectedPlayer && availablePlayers.length > 0) {
            setSelectedPlayer(availablePlayers[0]);
        }
    }, [availablePlayers, selectedPlayer]);

    // Fetch sessions specifically for the selected player
    useEffect(() => {
        if (!db || !selectedPlayer) {
            setSessionsWithVideo([]);
            return;
        }
        setIsLoading(true);
        // Query the player's training sessions
        const q = query(collection(db, `artifacts/${appId}/public/data/profiles/${selectedPlayer.userId}/training_sessions`), orderBy('scheduledDate', 'desc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                // Filter only sessions that have a video URL or array attached
                // Support both legacy single 'sessionVideoUrl' and new 'sessionVideos' array
                const hasLegacy = !!data.sessionVideoUrl;
                const hasArray = Array.isArray(data.sessionVideos) && data.sessionVideos.length > 0;
                
                if (hasLegacy || hasArray) {
                    // Normalize to array for display
                    let videos = [];
                    if (hasArray) videos = data.sessionVideos;
                    else if (hasLegacy) videos = [data.sessionVideoUrl];
                    
                    list.push({ id: doc.id, ...data, displayVideos: videos });
                }
            });
            setSessionsWithVideo(list);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching video archive:", err);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, selectedPlayer, appId]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <h4 className="text-2xl font-semibold text-gray-800">Session Video Archive</h4>
                
                {/* Player Selector (Hidden for Player, Visible for Coach/Admin/Parent) */}
                {role !== 'Player' && (
                    <div className="w-full md:w-64">
                        <select 
                            value={selectedPlayer?.userId || ''} 
                            onChange={(e) => setSelectedPlayer(availablePlayers.find(p => p.userId === e.target.value) || null)}
                            className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                        >
                            {availablePlayers.map(p => (
                                <option key={p.userId} value={p.userId}>
                                    {p.firstName} {p.lastName}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Video Grid */}
            {isLoading ? <div className="text-center py-10 text-gray-500">Loading videos...</div> : (
                <>
                    {sessionsWithVideo.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl">
                            <p className="text-gray-500">No video sessions found for {selectedPlayer?.firstName || 'this player'}.</p>
                            {(role === 'Coach' || role === 'Admin') && <p className="text-sm text-gray-400 mt-2">Go to the "Training Planner" to attach videos to sessions.</p>}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sessionsWithVideo.map(session => (
                                session.displayVideos.map((videoUrl, idx) => {
                                    const thumbnail = getYouTubeThumbnail(videoUrl);
                                    return (
                                        <div key={`${session.id}-${idx}`} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow">
                                            {/* Thumbnail Area */}
                                            <div className="relative bg-gray-900 h-48 flex items-center justify-center overflow-hidden">
                                                {thumbnail ? (
                                                    <img src={thumbnail} alt="Session Video" className="w-full h-full object-cover opacity-80" />
                                                ) : (
                                                    <div className="text-white text-4xl">▶</div>
                                                )}
                                                <a 
                                                    href={videoUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-10 transition-all group"
                                                >
                                                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                                        <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                                    </div>
                                                </a>
                                            </div>
                                            
                                            {/* Details Area */}
                                            <div className="p-4">
                                                <div className="flex justify-between items-start mb-2">
                                                    <span className="inline-block px-2 py-1 text-xs font-bold bg-indigo-100 text-indigo-700 rounded uppercase">
                                                        {session.scheduledDate}
                                                    </span>
                                                    {session.displayVideos.length > 1 && <span className="text-xs text-gray-400">Video {idx + 1} of {session.displayVideos.length}</span>}
                                                </div>
                                                <h5 className="font-bold text-gray-800 mb-1">Training Session</h5>
                                                <p className="text-sm text-gray-500 line-clamp-2">
                                                    {session.adminNotes || "No specific notes for this session."}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// --- NEW FEATURE: Skill Radar Chart ---

const SkillRadarChart = ({ reports }) => {
    const data = useMemo(() => {
        // Default values
        const stats = { Batting: 0, Bowling: 0, Fielding: 0, Fitness: 0, Mental: 0 };
        const counts = { Batting: 0, Bowling: 0, Fielding: 0, Fitness: 0, Mental: 0 };

        reports.forEach(r => {
            if (r.skillRatings) {
                Object.keys(r.skillRatings).forEach(cat => {
                    if (r.skillRatings[cat] > 0) {
                        stats[cat] += parseInt(r.skillRatings[cat]);
                        counts[cat]++;
                    }
                });
            }
        });

        return Object.keys(stats).map(key => ({
            subject: key,
            A: counts[key] > 0 ? (stats[key] / counts[key]).toFixed(1) : 0,
            fullMark: 10
        }));
    }, [reports]);

    const hasData = data.some(d => d.A > 0);

    if (!hasData) return <div className="p-6 text-center text-gray-500 border rounded-lg">No skill rating data available yet. Coaches can add ratings in new progress reports.</div>;

    return (
        <div className="w-full h-80 bg-white border rounded-xl shadow-sm p-4 flex flex-col items-center justify-center">
            <h5 className="text-lg font-bold text-gray-700 mb-2">Player Attribute Profile</h5>
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} />
                    <Radar name="Skill Rating" dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                    <Tooltip />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
};

// --- Existing Components (Updated) ---

const AdminAnnouncementComposer = ({ db, profile, allUsers, onShowMessage }) => {
    const appId = STATIC_APP_ID;
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [targetType, setTargetType] = useState('global'); 
    const [targetValue, setTargetValue] = useState('All Users'); 
    const [isSending, setIsSending] = useState(false);
    const [allFutureSessions, setAllFutureSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);

    const coaches = useMemo(() => {
        return allUsers.filter(u => u.isActive && safeRoles(u).includes('Coach'));
    }, [allUsers]);

    useEffect(() => {
        const fetchAllFutureSessions = async () => {
            if (targetType !== 'session') return; 
            setIsLoadingSessions(true);
            const todayStr = formatDate(new Date());
            const activePlayers = allUsers.filter(u => u.isActive && safeRoles(u).includes('Player'));
            let allSessions = [];
            try {
                const sessionPromises = activePlayers.map(async (player) => {
                    const sessionsRef = collection(db, `artifacts/${appId}/public/data/profiles/${player.userId}/training_sessions`);
                    const q = query(sessionsRef, orderBy('scheduledDate', 'desc'));
                    const querySnapshot = await getDocs(q);
                    const playerSessions = [];
                    querySnapshot.forEach(doc => {
                        const session = { id: doc.id, ...doc.data() };
                        if (session.scheduledDate >= todayStr) {
                            const assignedCoachIds = player.assignedCoaches || [];
                            const coachNames = assignedCoachIds.map(coachId => {
                                const coach = allUsers.find(u => u.userId === coachId);
                                return coach ? `${coach.firstName} ${coach.lastName}` : 'Unknown';
                            }).join(', ');
                            playerSessions.push({
                                ...session,
                                coachNames: coachNames || 'N/A',
                                playerName: `${player.firstName} ${player.lastName}`,
                                playerId: player.userId 
                            });
                        }
                    });
                    return playerSessions;
                });
                const results = await Promise.all(sessionPromises);
                allSessions = results.flat();
                allSessions.sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate));
                setAllFutureSessions(allSessions);
            } catch (error) {
                console.error("Error fetching all sessions:", error);
                onShowMessage(`Error fetching sessions: ${error.message}`);
            } finally {
                setIsLoadingSessions(false);
            }
        };
        fetchAllFutureSessions();
    }, [db, allUsers, onShowMessage, appId, targetType]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!title || !message) {
            onShowMessage("Title and Message are required.");
            return;
        }
        setIsSending(true);
        let recipientIds = new Set();
        let targetDescription = 'Global (All Users)';

        try {
            if (targetType === 'global') {
                allUsers.forEach(u => recipientIds.add(u.userId));
            } else if (targetType === 'role') {
                allUsers.forEach(u => {
                    if (safeRoles(u).includes(targetValue)) recipientIds.add(u.userId);
                });
                targetDescription = `Role: ${targetValue}`;
            } else if (targetType === 'coach_group') {
                const coachId = targetValue;
                const coach = coaches.find(c => c.userId === coachId);
                targetDescription = `Coach Group: ${coach.firstName} ${coach.lastName}`;
                recipientIds.add(coachId);
                allUsers.forEach(u => {
                    if (Array.isArray(u.assignedCoaches) && u.assignedCoaches.includes(coachId)) {
                        recipientIds.add(u.userId);
                        if (Array.isArray(u.assignedParents)) u.assignedParents.forEach(parentId => recipientIds.add(parentId));
                    }
                });
            } else if (targetType === 'session') {
                if (!targetValue) { onShowMessage("Please select a session."); setIsSending(false); return; }
                const { playerId, scheduledDate, playerName } = JSON.parse(targetValue);
                const player = allUsers.find(u => u.userId === playerId);
                if (player) {
                    recipientIds.add(player.userId);
                    if (Array.isArray(player.assignedParents)) player.assignedParents.forEach(pId => recipientIds.add(pId));
                }
                targetDescription = `Session: ${playerName} on ${scheduledDate}`;
            }

            if (recipientIds.size === 0) {
                onShowMessage("No recipients found for this target.");
                setIsSending(false);
                return;
            }

            const announcementData = {
                title, message, senderName: `${profile.firstName} ${profile.lastName} (Admin)`, createdAt: Timestamp.now(), isRead: false
            };

            const logCollectionRef = collection(db, `artifacts/${appId}/public/data/announcements_log`);
            await addDoc(logCollectionRef, { ...announcementData, target: targetDescription, recipientCount: recipientIds.size, sentBy: profile.userId });

            const deliveryPromises = [];
            recipientIds.forEach(userId => {
                const mailboxRef = collection(db, `artifacts/${appId}/public/data/profiles/${userId}/announcements`);
                deliveryPromises.push(addDoc(mailboxRef, announcementData));
            });
            await Promise.all(deliveryPromises);
            onShowMessage(`Announcement sent to ${recipientIds.size} users.`);
            setTitle(''); setMessage(''); setTargetType('global'); setTargetValue('All Users');
        } catch (error) {
            console.error("Error sending announcement:", error);
            onShowMessage(`Failed to send announcement: ${error.message}`);
        } finally {
            setIsSending(false);
        }
    };

    const renderTargetValueSelector = () => {
        if (targetType === 'global') return <p className="text-gray-500">Sends to all users in the system.</p>;
        if (targetType === 'role') {
            return (
                <select value={targetValue} onChange={(e) => setTargetValue(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="Player">All Players</option>
                    <option value="Coach">All Coaches</option>
                    <option value="Parent">All Parents</option>
                </select>
            );
        }
        if (targetType === 'coach_group') {
            return (
                <select value={targetValue} onChange={(e) => setTargetValue(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">-- Select Coach --</option>
                    {coaches.map(c => <option key={c.userId} value={c.userId}>{c.firstName} {c.lastName} ({c.email})</option>)}
                </select>
            );
        }
        if (targetType === 'session') {
            if (isLoadingSessions) return <p className="text-gray-500">Loading sessions...</p>;
            if (allFutureSessions.length === 0) return <p className="text-gray-500">No future sessions found.</p>;
            return (
                <select value={targetValue} onChange={(e) => setTargetValue(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">-- Select Session --</option>
                    {allFutureSessions.map(s => (
                        <option key={s.id} value={JSON.stringify({ playerId: s.playerId, sessionId: s.id, scheduledDate: s.scheduledDate, playerName: s.playerName })}>
                            {s.scheduledDate} - {s.playerName} (Coach: {s.coachNames})
                        </option>
                    ))}
                </select>
            );
        }
    };

    return (
        <form onSubmit={handleSend} className="p-6 bg-white rounded-lg border border-gray-200 space-y-4 shadow-md">
            <h5 className="text-xl font-bold text-indigo-600">Compose Announcement</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Send To</label>
                    <select value={targetType} onChange={(e) => { setTargetType(e.target.value); if (e.target.value === 'global') setTargetValue('All Users'); if (e.target.value === 'role') setTargetValue('Player'); if (e.target.value === 'coach_group') setTargetValue(''); if (e.target.value === 'session') setTargetValue(''); }} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500">
                        <option value="global">Global (All Users)</option>
                        <option value="role">By Role</option>
                        <option value="coach_group">By Coach Group</option>
                        <option value="session">By Session</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Target</label>
                    {renderTargetValueSelector()}
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g., Practice Cancelled Today" required />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows="5" className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="Write your announcement here..." required ></textarea>
            </div>
            <button type="submit" disabled={isSending || (targetType === 'session' && isLoadingSessions)} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-md transition duration-150 disabled:opacity-50">
                {isSending ? 'Sending...' : 'Send Announcement'}
            </button>
        </form>
    );
};

const AdminAnnouncementLog = ({ db, onShowMessage }) => {
    const appId = STATIC_APP_ID;
    const [log, setLog] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const logCollectionRef = collection(db, `artifacts/${appId}/public/data/announcements_log`);
        const q = query(logCollectionRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const fetchedLogs = [];
            querySnapshot.forEach((doc) => fetchedLogs.push({ id: doc.id, ...doc.data() }));
            setLog(fetchedLogs);
            setIsLoading(false);
        }, (error) => {
            onShowMessage(`Error fetching log: ${error.message}`);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, appId, onShowMessage]);

    return (
        <div className="space-y-4">
            <h5 className="text-xl font-bold text-gray-800">Sent Announcements Log</h5>
            {isLoading ? <p>Loading log...</p> : log.length === 0 ? <p className="text-gray-500">No announcements sent yet.</p> : log.map(item => (
                <div key={item.id} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex justify-between items-center">
                        <h6 className="font-bold text-gray-900">{item.title}</h6>
                        <span className="text-xs text-gray-500">{formatTimestamp(item.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-2">{item.message}</p>
                    <div className="text-xs text-gray-500 mt-3 pt-3 border-t">
                        Sent by {item.senderName} to {item.target} ({item.recipientCount} users)
                    </div>
                </div>
            ))}
        </div>
    );
};

const AdminAnnouncements = ({ db, profile, allUsers, onShowMessage }) => (
    <div className="space-y-8">
        <AdminAnnouncementComposer db={db} profile={profile} allUsers={allUsers} onShowMessage={onShowMessage} />
        <AdminAnnouncementLog db={db} onShowMessage={onShowMessage} />
    </div>
);

const UserAnnouncements = ({ db, profile, onShowMessage }) => {
    const appId = STATIC_APP_ID;
    const [announcements, setAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!profile) return;
        const mailboxRef = collection(db, `artifacts/${appId}/public/data/profiles/${profile.userId}/announcements`);
        const q = query(mailboxRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const fetchedMessages = [];
            querySnapshot.forEach((doc) => fetchedMessages.push({ id: doc.id, ...doc.data() }));
            setAnnouncements(fetchedMessages);
            setIsLoading(false);
        }, (error) => {
            onShowMessage(`Error fetching announcements: ${error.message}`);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, appId, profile, onShowMessage]);

    return (
        <div className="space-y-4">
            <h4 className="text-2xl font-semibold text-gray-800">Announcements</h4>
            {isLoading ? <p>Loading messages...</p> : announcements.length === 0 ? <p className="text-gray-500">You have no announcements.</p> : announcements.map(item => (
                <div key={item.id} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex justify-between items-center">
                        <h6 className="font-bold text-gray-900">{item.title}</h6>
                        <span className="text-xs text-gray-500">{formatTimestamp(item.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{item.message}</p>
                    <div className="text-xs text-gray-500 mt-3 pt-3 border-t">From: {item.senderName}</div>
                </div>
            ))}
        </div>
    );
};

const ATTENDANCE_STATUSES = ['Present', 'Absent', 'Excused'];
const AttendanceTracker = ({ db, roster, currentUserId, onShowMessage }) => {
    const initialDate = formatDate(new Date());
    const [selectedDate, setSelectedDate] = useState(initialDate);
    const [dailyRecords, setDailyRecords] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const appId = STATIC_APP_ID;
    const activePlayers = useMemo(() => [...roster].sort((a, b) => a.email.localeCompare(b.email)), [roster]);

    useEffect(() => {
        if (!db || !selectedDate || activePlayers.length === 0) {
            setDailyRecords([]);
            return;
        }
        const docRef = doc(db, `artifacts/${appId}/public/data/attendance`, selectedDate);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            const fetchedRecords = docSnap.exists() ? docSnap.data().records : [];
            const mergedRecords = activePlayers.map(player => {
                const existing = fetchedRecords.find(r => r.userId === player.userId);
                return {
                    userId: player.userId,
                    email: player.email,
                    firstName: player.firstName, 
                    lastName: player.lastName, 
                    status: existing ? existing.status : 'Absent', 
                    markedBy: existing ? existing.markedBy : ''
                };
            });
            setDailyRecords(mergedRecords);
        }, (error) => {
            console.error("Error fetching attendance:", error);
            onShowMessage(`Error fetching attendance for ${selectedDate}: ${error.message}.`);
        });
        return () => unsubscribe();
    }, [db, appId, selectedDate, activePlayers, onShowMessage]);

    const handleStatusChange = useCallback((userId, newStatus) => {
        setDailyRecords(prevRecords => prevRecords.map(record =>
            record.userId === userId ? { ...record, status: newStatus, markedBy: currentUserId } : record
        ));
    }, [currentUserId]);

    const handleSaveAttendance = async () => {
        if (dailyRecords.length === 0) return;
        setIsSaving(true);
        const recordsToSave = dailyRecords.map(r => ({
            userId: r.userId, status: r.status, markedBy: r.markedBy, timestamp: new Date().toISOString()
        }));
        const docRef = doc(db, `artifacts/${appId}/public/data/attendance`, selectedDate);
        try {
            await setDoc(docRef, { date: selectedDate, records: recordsToSave });
            onShowMessage(`Attendance for ${selectedDate} saved successfully!`);
        } catch (error) {
            console.error("Error saving attendance:", error);
            onShowMessage(`Failed to save attendance: ${error.message}.`);
        } finally {
            setIsSaving(false);
        }
    };
     
    return (
        <div className="space-y-6">
            <h4 className="text-2xl font-semibold text-gray-800">Attendance Tracking ({roster.length})</h4>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0 p-4 bg-white rounded-lg shadow-md border border-gray-200">
                <div className="flex items-center space-x-3">
                    <label className="text-gray-700 font-medium">Date:</label>
                    <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} max={initialDate} className="p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <button onClick={handleSaveAttendance} disabled={dailyRecords.length === 0 || isSaving} className="py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-md transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSaving ? 'Saving...' : 'Save Attendance'}
                </button>
            </div>
            {activePlayers.length > 0 ? (
                <div className="overflow-x-auto shadow-lg rounded-xl">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Attendance Status</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Marked By</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {dailyRecords.map(record => (
                                <tr key={record.userId} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {record.firstName || ''} {record.lastName || ''}
                                        <div className="text-xs text-gray-500">{record.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <select value={record.status} onChange={(e) => handleStatusChange(record.userId, e.target.value)} className={`p-2 border rounded-lg shadow-sm transition-colors ${record.status === 'Present' ? 'bg-green-100 border-green-400 text-green-800' : record.status === 'Excused' ? 'bg-yellow-100 border-yellow-400 text-yellow-800' : 'bg-red-100 border-red-400 text-red-800'}`}>
                                            {ATTENDANCE_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-xs text-gray-500">{record.markedBy ? `${record.markedBy.substring(0, 8)}...` : 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-center text-gray-500 py-10">No active players found to track attendance.</p>
            )}
        </div>
    );
};

const AdminMetricsManager = ({ db, onShowMessage, allMetrics }) => {
    const appId = STATIC_APP_ID;
    const [isLoading, setIsLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [name, setName] = useState('');
    const [unit, setUnit] = useState('');
    const [category, setCategory] = useState(METRIC_CATEGORIES[0]);
    const [metricToDelete, setMetricToDelete] = useState(null);

    const handleAddMetric = async (e) => {
        e.preventDefault();
        if (!name || !unit || !category) { onShowMessage("Please fill out all fields."); return; }
        setIsLoading(true);
        try {
            const metricDocRef = doc(db, `artifacts/${appId}/public/data/metrics`, name.toLowerCase().replace(/\s+/g, '_'));
            await setDoc(metricDocRef, { name, unit, category });
            onShowMessage(`Metric "${name}" added successfully.`);
            setName(''); setUnit(''); setCategory(METRIC_CATEGORIES[0]); setShowForm(false);
        } catch (error) {
            console.error("Error adding metric:", error);
            onShowMessage(`Failed to add metric: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const executeDeleteMetric = async () => {
        if (!metricToDelete) return;
        setIsLoading(true);
        try {
            const metricDocRef = doc(db, `artifacts/${appId}/public/data/metrics`, metricToDelete.id);
            await deleteDoc(metricDocRef);
            onShowMessage(`Metric "${metricToDelete.name}" deleted successfully.`);
        } catch (error) {
            console.error("Error deleting metric:", error);
            onShowMessage(`Failed to delete metric: ${error.message}`);
        } finally {
            setIsLoading(false);
            setMetricToDelete(null); 
        }
    };
     
    const groupedMetrics = useMemo(() => {
        return allMetrics.reduce((acc, metric) => {
            const cat = metric.category || 'Other';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(metric);
            return acc;
        }, {});
    }, [allMetrics]);

    return (
        <> 
            <ConfirmationModal isOpen={!!metricToDelete} message={`Are you sure you want to delete the metric "${metricToDelete?.name}"? This cannot be undone.`} onConfirm={executeDeleteMetric} onCancel={() => setMetricToDelete(null)} />
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h4 className="text-2xl font-semibold text-gray-800">Metrics Library ({allMetrics.length} total)</h4>
                    <button onClick={() => setShowForm(!showForm)} className={`py-2 px-4 font-semibold rounded-lg shadow-md transition duration-150 ${showForm ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}>{showForm ? 'Cancel' : '+ Add New Metric'}</button>
                </div>
                {showForm && (
                    <form onSubmit={handleAddMetric} className="p-6 bg-gray-50 rounded-lg border border-gray-200 space-y-4 shadow-inner">
                        <h5 className="text-xl font-semibold text-gray-700">New Metric Details</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block text-sm font-medium text-gray-700">Metric Name</label><input type="text" placeholder="e.g., 20m Sprint" value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" required /></div>
                            <div><label className="block text-sm font-medium text-gray-700">Unit of Measure</label><input type="text" placeholder="e.g., seconds" value={unit} onChange={(e) => setUnit(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" required /></div>
                            <div><label className="block text-sm font-medium text-gray-700">Category</label><select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" required >{METRIC_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></div>
                        </div>
                        <button type="submit" disabled={isLoading} className="py-2 px-5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-md transition duration-150 disabled:opacity-50">{isLoading ? 'Saving...' : 'Save Metric'}</button>
                    </form>
                )}
                <div className="space-y-4">
                    {Object.keys(groupedMetrics).sort().map(categoryName => (
                        <div key={categoryName} className="p-4 border rounded-lg bg-white">
                            <h6 className="text-lg font-bold text-indigo-600 mb-3">{categoryName}</h6>
                            <div className="divide-y divide-gray-100">
                                {groupedMetrics[categoryName].map(metric => (
                                    <div key={metric.id} className="flex justify-between items-center py-2">
                                        <div><span className="text-gray-900 font-medium">{metric.name}</span><span className="text-gray-500 text-sm ml-2">({metric.unit})</span></div>
                                        <button onClick={() => setMetricToDelete(metric)} disabled={isLoading} className="text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-50">Delete</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

const NewProgressReportForm = ({ db, player, coach, allMetrics, onShowMessage, onClose, plannedMetrics = null, plannedNotes = "", sessionToUpdateId = null }) => {
    const appId = STATIC_APP_ID;
    const [isLoading, setIsLoading] = useState(false);
    const [sessionType, setSessionType] = useState(plannedMetrics ? 'Planned Session' : 'General Session');
    const [notesStrengths, setNotesStrengths] = useState('');
    const [notesImprovement, setNotesImprovement] = useState('');
    const [nextFocus, setNextFocus] = useState('');
    const [metricValues, setMetricValues] = useState({}); 
    // NEW: Skill Ratings
    const [skillRatings, setSkillRatings] = useState({ Batting: 5, Bowling: 5, Fielding: 5, Fitness: 5, Mental: 5 });

    const handleMetricChange = (metricName, value) => {
        setMetricValues(prev => ({ ...prev, [metricName]: value }));
    };

    const handleSubmitReport = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const reportData = {
            playerId: player.userId,
            coachId: coach.uid,
            coachName: `${coach.profile.firstName || ''} ${coach.profile.lastName || ''}`.trim(),
            date: Timestamp.now(), 
            sessionType,
            notes_strengths: notesStrengths,
            notes_improvement: notesImprovement,
            next_focus: nextFocus,
            metrics: metricValues, 
            skillRatings: skillRatings, // Save radar data
            linkedSessionId: sessionToUpdateId || null,
            assessmentType: "coach" 
        };

        try {
            const reportsCollectionRef = collection(db, `artifacts/${appId}/public/data/profiles/${player.userId}/progress_reports`);
            const reportDocRef = await addDoc(reportsCollectionRef, reportData);
            if (sessionToUpdateId) {
                const sessionDocRef = doc(db, `artifacts/${appId}/public/data/profiles/${player.userId}/training_sessions`, sessionToUpdateId);
                await updateDoc(sessionDocRef, { status: "Completed", linkedReportId: reportDocRef.id });
            }
            onShowMessage(`Progress report for ${player.firstName} saved successfully.`);
            onClose(); 
        } catch (error) {
            console.error("Error saving report:", error);
            onShowMessage(`Failed to save report: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const metricsToDisplay = useMemo(() => {
        if (!plannedMetrics) return allMetrics;
        const plannedSet = new Set(plannedMetrics);
        return allMetrics.filter(m => plannedSet.has(m.id));
    }, [allMetrics, plannedMetrics]);

    const groupedMetrics = useMemo(() => {
        return metricsToDisplay.reduce((acc, metric) => {
            const cat = metric.category || 'Other';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(metric);
            return acc;
        }, {});
    }, [metricsToDisplay]);

    return (
        <form onSubmit={handleSubmitReport} className="p-6 bg-white rounded-lg border border-gray-200 space-y-6 shadow-xl relative">
            <button onClick={onClose} type="button" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">&times;</button>
            <h5 className="text-2xl font-bold text-indigo-600">New Report for {player.firstName} {player.lastName}</h5>
            {plannedNotes && <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"><label className="block text-sm font-medium text-yellow-700">Admin's Focus Notes</label><p className="text-gray-700 mt-1">{plannedNotes}</p></div>}
            <div><label className="block text-sm font-medium text-gray-700">Session Type</label><input type="text" value={sessionType} onChange={(e) => setSessionType(e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" required /></div>
             
            {/* NEW: Skill Ratings */}
            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg space-y-4">
                <h6 className="font-bold text-blue-800">Skill Ratings (1-10) - Updates Player Profile Chart</h6>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {SKILL_CATEGORIES.map(cat => (
                        <div key={cat}>
                            <label className="block text-xs font-bold text-gray-600 mb-1">{cat}</label>
                            <input type="number" min="1" max="10" value={skillRatings[cat]} onChange={e => setSkillRatings({...skillRatings, [cat]: e.target.value})} className="w-full p-2 border rounded text-center" />
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700">Strengths Observed</label><textarea value={notesStrengths} onChange={(e) => setNotesStrengths(e.target.value)} rows="3" className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="What went well?" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Areas for Improvement</label><textarea value={notesImprovement} onChange={(e) => setNotesImprovement(e.target.value)} rows="3" className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="What needs work?" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Next Focus Point</label><textarea value={nextFocus} onChange={(e) => setNextFocus(e.target.value)} rows="2" className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="Next session focus..." /></div>
            </div>
            <div className="space-y-4">
                <h6 className="text-lg font-semibold text-gray-700">{plannedMetrics ? 'Planned Metrics' : 'Quantitative Metrics'}</h6>
                {metricsToDisplay.length === 0 && <p className="text-gray-500">No metrics were planned for this session.</p>}
                {Object.keys(groupedMetrics).sort().map(categoryName => (
                    <div key={categoryName} className="p-4 border rounded-lg bg-gray-50">
                        <h6 className="text-md font-bold text-indigo-500 mb-3">{categoryName}</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {groupedMetrics[categoryName].map(metric => (
                                <div key={metric.id}>
                                    <label className="block text-sm font-medium text-gray-700">{metric.name} ({metric.unit})</label>
                                    <input type="text" value={metricValues[metric.name] || ''} onChange={(e) => handleMetricChange(metric.name, e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <button type="submit" disabled={isLoading} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-md transition duration-150 disabled:opacity-50">{isLoading ? 'Saving...' : 'Save Progress Report'}</button>
        </form>
    );
};

const PlayerSelfAssessmentForm = ({ db, player, allMetrics, onShowMessage, onClose, sessionToUpdateId = null, plannedMetrics = null }) => {
    const appId = STATIC_APP_ID;
    const [isLoading, setIsLoading] = useState(false);
    const [notesStrengths, setNotesStrengths] = useState('');
    const [notesImprovement, setNotesImprovement] = useState('');
    const [metricValues, setMetricValues] = useState({}); 

    const handleMetricChange = (metricName, value) => {
        setMetricValues(prev => ({ ...prev, [metricName]: value }));
    };

    const handleSubmitReport = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const reportData = {
            playerId: player.userId,
            coachId: null, 
            coachName: "Player Self-Assessment",
            date: Timestamp.now(),
            sessionType: "Self-Assessment",
            notes_strengths: notesStrengths,
            notes_improvement: notesImprovement,
            next_focus: "", 
            metrics: metricValues,
            linkedSessionId: sessionToUpdateId, // Linked if coming from Planner
            assessmentType: "player" 
        };

        try {
            const reportsCollectionRef = collection(db, `artifacts/${appId}/public/data/profiles/${player.userId}/progress_reports`);
            const reportDocRef = await addDoc(reportsCollectionRef, reportData);
            
            // If linked to a session, update the session document to reflect this
            if (sessionToUpdateId) {
                const sessionDocRef = doc(db, `artifacts/${appId}/public/data/profiles/${player.userId}/training_sessions`, sessionToUpdateId);
                // We use specific fields for player assessment so we don't overwrite Coach status/links
                await updateDoc(sessionDocRef, { 
                    playerAssessmentSubmitted: true, 
                    linkedPlayerReportId: reportDocRef.id 
                });
            }

            onShowMessage(`Self-Assessment saved successfully.`);
            onClose();
        } catch (error) {
            console.error("Error saving self-assessment:", error);
            onShowMessage(`Failed to save report: ${error.message}.`);
        } finally {
            setIsLoading(false);
        }
    };

    const metricsToDisplay = useMemo(() => {
        if (!plannedMetrics) return allMetrics;
        const plannedSet = new Set(plannedMetrics);
        return allMetrics.filter(m => plannedSet.has(m.id));
    }, [allMetrics, plannedMetrics]);

    const groupedMetrics = useMemo(() => {
        return metricsToDisplay.reduce((acc, metric) => {
            const cat = metric.category || 'Other';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(metric);
            return acc;
        }, {});
    }, [metricsToDisplay]);

    return (
        <form onSubmit={handleSubmitReport} className="p-6 bg-white rounded-lg border border-gray-200 space-y-6 shadow-xl relative">
            <button onClick={onClose} type="button" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">&times;</button>
            <h5 className="text-2xl font-bold text-blue-600">
                {sessionToUpdateId ? 'Session Self-Assessment' : 'New Self-Assessment'}
            </h5>
            <div className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700">What went well? (Strengths)</label><textarea value={notesStrengths} onChange={(e) => setNotesStrengths(e.target.value)} rows="3" className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="What did you feel good about?" /></div>
                <div><label className="block text-sm font-medium text-gray-700">What needs work? (Improvements)</label><textarea value={notesImprovement} onChange={(e) => setNotesImprovement(e.target.value)} rows="3" className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="What will you focus on next time?" /></div>
            </div>
            <div className="space-y-4">
                <h6 className="text-lg font-semibold text-gray-700">{plannedMetrics ? 'Planned Metrics' : 'Rate Your Performance'}</h6>
                {Object.keys(groupedMetrics).length === 0 && <p className="text-gray-500 italic">No specific metrics were planned for this session.</p>}
                {Object.keys(groupedMetrics).sort().map(categoryName => (
                    <div key={categoryName} className="p-4 border rounded-lg bg-gray-50">
                        <h6 className="text-md font-bold text-indigo-500 mb-3">{categoryName}</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {groupedMetrics[categoryName].map(metric => (
                                <div key={metric.id}>
                                    <label className="block text-sm font-medium text-gray-700">{metric.name} ({metric.unit})</label>
                                    <input type="text" value={metricValues[metric.name] || ''} onChange={(e) => handleMetricChange(metric.name, e.target.value)} className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <button type="submit" disabled={isLoading} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-md transition duration-150 disabled:opacity-50">{isLoading ? 'Saving...' : 'Save Self-Assessment'}</button>
        </form>
    );
};

const PlayerProgressViewer = ({ db, onShowMessage, player, coach, allMetrics, isReadOnly = false, onShowGeneralReportForm }) => {
    const appId = STATIC_APP_ID;
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedMetric, setSelectedMetric] = useState(null);

    useEffect(() => {
        if (!db || !player) return;
        setIsLoading(true);
        const reportsCollectionRef = collection(db, `artifacts/${appId}/public/data/profiles/${player.userId}/progress_reports`);
        const q = query(reportsCollectionRef, orderBy('date', 'desc'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const fetchedReports = [];
            querySnapshot.forEach((doc) => fetchedReports.push({ id: doc.id, ...doc.data() }));
            setReports(fetchedReports);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching progress reports:", error);
            onShowMessage(`Error fetching reports: ${error.message}.`);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, appId, player, onShowMessage]);

    const uniqueMetrics = useMemo(() => {
        const metricsSet = new Set();
        reports.forEach(report => {
            if (report.metrics) {
                Object.keys(report.metrics).forEach(metricName => {
                    if (report.metrics[metricName] && !isNaN(parseFloat(report.metrics[metricName]))) {
                        metricsSet.add(metricName);
                    }
                });
            }
        });
        return Array.from(metricsSet).sort();
    }, [reports]);

    const chartData = useMemo(() => {
        if (!selectedMetric) return [];
        const reportsWithNumericData = reports
            .map(report => ({
                report: report,
                value: (report.metrics && report.metrics[selectedMetric]) ? parseFloat(report.metrics[selectedMetric]) : NaN,
                date: report.date, 
                type: report.assessmentType || 'coach' 
            }))
            .filter(item => item.date && !isNaN(item.value)); 

        const dataMap = new Map();
        reportsWithNumericData.forEach(item => {
            const dateStr = item.date.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            if (!dataMap.has(dateStr)) {
                dataMap.set(dateStr, { date: dateStr, coachValue: null, playerValue: null, originalDate: item.date.toDate() });
            }
            const entry = dataMap.get(dateStr);
            if (item.type === 'player') entry.playerValue = item.value;
            else entry.coachValue = item.value;
        });

        const finalChartData = Array.from(dataMap.values());
        finalChartData.sort((a, b) => a.originalDate.getTime() - b.originalDate.getTime());
        return finalChartData.map(item => ({ date: item.date.split(',')[0], coachValue: item.coachValue, playerValue: item.playerValue }));
    }, [reports, selectedMetric]);

    const hasPlottableData = useMemo(() => {
        if (!chartData) return false;
        const coachPoints = chartData.filter(d => d.coachValue !== null).length;
        const playerPoints = chartData.filter(d => d.playerValue !== null).length;
        return coachPoints > 1 || playerPoints > 1;
    }, [chartData]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h4 className="text-2xl font-semibold text-gray-800">Player Progress for {player.firstName} {player.lastName}</h4>
                {!isReadOnly && (
                    <button onClick={onShowGeneralReportForm} className="py-2 px-4 font-semibold rounded-lg shadow-md transition duration-150 bg-green-600 hover:bg-green-700 text-white">
                        + Create General Report
                    </button>
                )}
            </div>
             
            {/* NEW: Radar Chart */}
            {reports.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1">
                        <SkillRadarChart reports={reports} />
                    </div>
                    <div className="lg:col-span-2">
                        {/* Metric Line Chart */}
                        <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 h-80">
                            <h6 className="text-lg font-bold text-gray-700 mb-2">Metric Trends</h6>
                            {uniqueMetrics.length === 0 ? <p className="text-gray-500 text-center pt-10">No metric data found.</p> : (
                                <>
                                    <div className="mb-2"><select value={selectedMetric || ''} onChange={(e) => setSelectedMetric(e.target.value)} className="w-full p-2 border border-gray-300 rounded"><option value="">-- Select Metric --</option>{uniqueMetrics.map(metric => <option key={metric} value={metric}>{metric}</option>)}</select></div>
                                    {selectedMetric && (hasPlottableData ? (
                                        <div style={{ width: '100%', height: 200 }}>
                                            <ResponsiveContainer><LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" fontSize={12} /><YAxis /><Tooltip /><Legend /><Line type="monotone" dataKey="coachValue" name="Coach" stroke="#8884d8" activeDot={{ r: 8 }} connectNulls /><Line type="monotone" dataKey="playerValue" name="Player" stroke="#82ca9d" activeDot={{ r: 8 }} connectNulls /></LineChart></ResponsiveContainer>
                                        </div>
                                    ) : <p className="text-center text-gray-500 pt-10">Not enough data to plot.</p>)}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isLoading ? <div className="text-center text-gray-500 py-10">Loading reports...</div> : reports.length === 0 ? <p className="text-center text-gray-500 py-10 border border-dashed rounded-lg">No progress reports have been saved for this player yet.</p> : (
                <div className="space-y-6">
                    {reports.map(report => (
                        <div key={report.id} className="p-6 bg-white rounded-lg shadow-lg border border-gray-200">
                            <div className="flex justify-between items-center mb-4 pb-4 border-b">
                                <div><h6 className="text-xl font-bold text-indigo-600">{report.sessionType}</h6><p className="text-sm text-gray-500">Report by {report.coachName} on {formatTimestamp(report.date)}</p></div>
                                {report.linkedSessionId && <span className="text-xs font-medium bg-indigo-100 text-indigo-700 py-1 px-3 rounded-full">Planned Session</span>}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h6 className="font-semibold text-gray-700">Qualitative Notes</h6>
                                    <div><label className="block text-xs font-medium text-green-600">Strengths</label><p className="text-gray-800 p-2 bg-green-50 rounded border border-green-200">{report.notes_strengths || 'N/A'}</p></div>
                                    <div><label className="block text-xs font-medium text-yellow-600">Areas for Improvement</label><p className="text-gray-800 p-2 bg-yellow-50 rounded border border-yellow-200">{report.notes_improvement || 'N/A'}</p></div>
                                    <div><label className="block text-xs font-medium text-blue-600">Next Focus</label><p className="text-gray-800 p-2 bg-blue-50 rounded border border-blue-200">{report.next_focus || 'N/A'}</p></div>
                                </div>
                                <div className="space-y-4">
                                    <h6 className="font-semibold text-gray-700">Metrics Recorded</h6>
                                    {report.metrics && Object.keys(report.metrics).length > 0 ? (
                                        <div className="p-4 bg-gray-50 rounded border border-gray-200 grid grid-cols-2 gap-3">
                                            {Object.keys(report.metrics).map(metricName => <div key={metricName}><label className="block text-xs font-medium text-gray-500">{metricName}</label><p className="text-gray-900 font-bold text-lg">{report.metrics[metricName]}</p></div>)}
                                        </div>
                                    ) : <p className="text-gray-500">No specific metrics were recorded.</p>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const UserRoleEditModal = ({ user, isOpen, onClose, onSave, currentUserId, onShowMessage }) => {
    const [selectedRoles, setSelectedRoles] = useState(user?.roles || []);
    const [isSaving, setIsSaving] = useState(false);
    useEffect(() => { setSelectedRoles(user?.roles || []); }, [user]);
    if (!isOpen || !user) return null;
    const handleRoleChange = (role) => {
        let newRoles = selectedRoles.includes(role) ? selectedRoles.filter(r => r !== role) : [...selectedRoles, role];
        newRoles.sort();
        setSelectedRoles(newRoles);
    };
    const handleSave = async () => {
        if (user.userId === currentUserId && !selectedRoles.includes('Admin')) { onShowMessage("Admin cannot remove their own Admin role."); return; }
        if (selectedRoles.length === 0) { onShowMessage("A user must have at least one role. Defaulting to 'Player'."); setSelectedRoles(['Player']); return; }
        setIsSaving(true);
        await onSave(user.userId, selectedRoles);
        setIsSaving(false);
        onClose();
    };
    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">Edit Roles</h3>
                <div className="space-y-2"><p className="font-medium">{user.firstName} {user.lastName}</p><p className="text-sm text-gray-600">{user.email}</p></div>
                <div className="space-y-3"><label className="block text-sm font-medium text-gray-700">Select Roles:</label><div className="flex flex-wrap gap-2">{ALL_ROLES.map(role => (<button key={role} onClick={() => handleRoleChange(role)} className={`py-2 px-4 rounded-full font-medium transition duration-150 text-sm ${selectedRoles.includes(role) ? 'bg-indigo-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-indigo-100'} ${(user.userId === currentUserId && role === 'Admin') ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={user.userId === currentUserId && role === 'Admin'}>{role}</button>))}</div></div>
                <div className="flex justify-end space-x-3"><button onClick={onClose} className="py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition duration-150">Cancel</button><button onClick={handleSave} disabled={isSaving} className="py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition duration-150 disabled:opacity-50">{isSaving ? 'Saving...' : 'Save Changes'}</button></div>
            </div>
        </div>
    );
};

const UserManagementList = ({ db, currentUserId, onShowMessage, allUsers }) => {
    const appId = STATIC_APP_ID;
    const [filter, setFilter] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const toggleActiveStatus = async (user) => {
        if (user.userId === currentUserId) { onShowMessage("You cannot disable your own account."); return; }
        const userDocRef = doc(db, `artifacts/${appId}/public/data/profiles`, user.userId);
        try {
            await updateDoc(userDocRef, { isActive: !user.isActive });
            onShowMessage(`User ${user.email} is now ${!user.isActive ? 'Enabled' : 'Disabled'}.`);
        } catch (error) { console.error("Error toggling status:", error); onShowMessage(`Failed: ${error.message}`); }
    };
    const updateRoles = async (userId, newRoles) => {
        const userDocRef = doc(db, `artifacts/${appId}/public/data/profiles`, userId);
        try { await updateDoc(userDocRef, { roles: newRoles }); onShowMessage(`Roles updated.`); } catch (error) { console.error(error); onShowMessage(`Failed: ${error.message}`); }
    };
    const filteredUsers = useMemo(() => {
        if (!filter) return allUsers;
        const lowerFilter = filter.toLowerCase();
        return allUsers.filter(user => user.email.toLowerCase().includes(lowerFilter) || (user.firstName || '').toLowerCase().includes(lowerFilter) || (user.lastName || '').toLowerCase().includes(lowerFilter));
    }, [allUsers, filter]);
    if (!allUsers) return <div className="text-center py-10">Loading...</div>;
    return (
        <>
            <div className="relative mb-6"><input type="text" placeholder="Search users..." value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full p-3 border rounded-lg" /></div>
            <div className="space-y-4">
                {filteredUsers.map((user) => (
                    <div key={user.userId} className="bg-white p-4 rounded-lg shadow-md border flex justify-between items-center">
                        <div><h4 className="font-bold">{user.firstName} {user.lastName}</h4><p className="text-sm text-gray-600">{user.email}</p><div className="flex gap-2 mt-1">{safeRoles(user).map(r => <span key={r} className="text-xs bg-gray-200 px-2 rounded">{r}</span>)}</div></div>
                        <div className="flex gap-2"><button onClick={() => setEditingUser(user)} className="px-3 py-1 bg-gray-200 rounded">Edit</button><button onClick={() => toggleActiveStatus(user)} className={`px-3 py-1 text-white rounded ${user.isActive ? 'bg-orange-500' : 'bg-green-600'}`}>{user.isActive ? 'Disable' : 'Enable'}</button></div>
                    </div>
                ))}
            </div>
            <UserRoleEditModal user={editingUser} isOpen={!!editingUser} onClose={() => setEditingUser(null)} onSave={updateRoles} currentUserId={currentUserId} onShowMessage={onShowMessage} />
        </>
    );
};

const UserRelationshipManager = ({ db, allUsers, onShowMessage }) => {
    const [selectedPlayerId, setSelectedPlayerId] = useState('');
    const [playerProfile, setPlayerProfile] = useState(null);
    const [selectedCoachId, setSelectedCoachId] = useState('');
    const [selectedParentId, setSelectedParentId] = useState('');
    const [players, coaches, parents] = useMemo(() => {
        const safeRoles = (u) => u.roles || [];
        return [allUsers.filter(u => u.isActive && safeRoles(u).includes('Player')), allUsers.filter(u => u.isActive && safeRoles(u).includes('Coach')), allUsers.filter(u => u.isActive && safeRoles(u).includes('Parent'))];
    }, [allUsers]);
    useEffect(() => { const p = players.find(u => u.userId === selectedPlayerId); setPlayerProfile(p || null); setSelectedCoachId(''); setSelectedParentId(''); }, [selectedPlayerId, players]);
    const handleRelationshipUpdate = async (type, userId, isAssigned) => {
        if (!playerProfile) return;
        const appId = STATIC_APP_ID;
        const playerDocRef = doc(db, `artifacts/${appId}/public/data/profiles`, playerProfile.userId);
        let fieldName = type === 'coach' ? 'assignedCoaches' : 'assignedParents';
        let current = Array.isArray(playerProfile[fieldName]) ? playerProfile[fieldName] : [];
        let newAssignments = isAssigned ? [...current, userId] : current.filter(id => id !== userId);
        try { await updateDoc(playerDocRef, { [fieldName]: [...new Set(newAssignments)] }); onShowMessage(`Updated.`); if(type==='coach') setSelectedCoachId(''); if(type==='parent') setSelectedParentId(''); } catch(e) { onShowMessage(e.message); }
    };
    if (players.length === 0) return <p>No active players.</p>;
    return (
        <div className="space-y-6">
            <h4 className="text-2xl font-semibold">Assign Relationships</h4>
            <select value={selectedPlayerId} onChange={(e) => setSelectedPlayerId(e.target.value)} className="w-full p-3 border rounded"><option value="">-- Choose Player --</option>{players.map(p => <option key={p.userId} value={p.userId}>{p.firstName} {p.lastName} ({p.email})</option>)}</select>
            {selectedPlayerId && playerProfile && (
                <div className="space-y-6 pt-4">
                    <div className="p-4 border rounded bg-white space-y-4"><h6 className="font-semibold">Assign Coach</h6><select value={selectedCoachId} onChange={(e) => setSelectedCoachId(e.target.value)} className="w-full p-2 border rounded"><option value="">-- Choose --</option>{coaches.filter(c => !(playerProfile.assignedCoaches||[]).includes(c.userId)).map(c => <option key={c.userId} value={c.userId}>{c.firstName} {c.lastName}</option>)}</select><button onClick={() => handleRelationshipUpdate('coach', selectedCoachId, true)} disabled={!selectedCoachId} className="w-full py-2 bg-orange-500 text-white rounded">Assign</button>
                    {(playerProfile.assignedCoaches||[]).map(id => { const c = coaches.find(u=>u.userId===id); return c ? <div key={id} className="flex justify-between items-center mt-2"><span>{c.firstName} {c.lastName}</span><button onClick={()=>handleRelationshipUpdate('coach', id, false)} className="text-red-500">Unassign</button></div> : null; })}</div>
                    <div className="p-4 border rounded bg-white space-y-4"><h6 className="font-semibold">Assign Parent</h6><select value={selectedParentId} onChange={(e) => setSelectedParentId(e.target.value)} className="w-full p-2 border rounded"><option value="">-- Choose --</option>{parents.filter(p => !(playerProfile.assignedParents||[]).includes(p.userId)).map(p => <option key={p.userId} value={p.userId}>{p.firstName} {p.lastName}</option>)}</select><button onClick={() => handleRelationshipUpdate('parent', selectedParentId, true)} disabled={!selectedParentId} className="w-full py-2 bg-orange-500 text-white rounded">Assign</button>
                    {(playerProfile.assignedParents||[]).map(id => { const p = parents.find(u=>u.userId===id); return p ? <div key={id} className="flex justify-between items-center mt-2"><span>{p.firstName} {p.lastName}</span><button onClick={()=>handleRelationshipUpdate('parent', id, false)} className="text-red-500">Unassign</button></div> : null; })}</div>
                </div>
            )}
        </div>
    );
};

const AdminTrainingPlanner = ({ db, allUsers, allMetrics, onShowMessage }) => {
    const appId = STATIC_APP_ID;
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState(null);
    // NEW STATE: Editing
    const [editingSession, setEditingSession] = useState(null);
    const [targetPlayerId, setTargetPlayerId] = useState(''); // NEW: Target player for duplication
     
    // Form States
    const [scheduledDate, setScheduledDate] = useState(formatDate(new Date()));
    const [adminNotes, setAdminNotes] = useState('');
    const [selectedMetrics, setSelectedMetrics] = useState(new Set());
     
    const activePlayers = useMemo(() => allUsers.filter(u => u.isActive && safeRoles(u).includes('Player')).sort((a, b) => a.email.localeCompare(b.email)), [allUsers]);
    const todayStr = formatDate(new Date());

    useEffect(() => {
        if (!db || !selectedPlayer) { 
            setSessions([]); 
            return; 
        }
        // Set default target player when selected player changes
        setTargetPlayerId(selectedPlayer.userId);

        const q = query(collection(db, `artifacts/${appId}/public/data/profiles/${selectedPlayer.userId}/training_sessions`), orderBy('scheduledDate', 'desc'));
        return onSnapshot(q, (snap) => { const list = []; snap.forEach(d => list.push({id:d.id, ...d.data()})); setSessions(list); });
    }, [db, selectedPlayer, appId]);

    const confirmDelete = async () => {
        if (!sessionToDelete) return;
        try { await deleteDoc(doc(db, `artifacts/${appId}/public/data/profiles/${sessionToDelete.playerId}/training_sessions`, sessionToDelete.id)); onShowMessage("Deleted."); } catch(e) { onShowMessage(e.message); } finally { setSessionToDelete(null); }
    };

    // Helper to reset form state
    const resetForm = () => {
        setScheduledDate(formatDate(new Date()));
        setAdminNotes('');
        setSelectedMetrics(new Set());
        setEditingSession(null);
        setShowForm(false);
        if (selectedPlayer) setTargetPlayerId(selectedPlayer.userId); // Reset target
    };

    // Handler to start editing
    const handleEditSession = (session) => {
        setEditingSession(session);
        setScheduledDate(session.scheduledDate);
        setAdminNotes(session.adminNotes || '');
        setSelectedMetrics(new Set(session.metricsToTrain || []));
        setTargetPlayerId(selectedPlayer.userId); // Ensure we edit for the current player
        setShowForm(true);
    };

    // NEW: Handler to duplicate a session
    const handleDuplicateSession = (session) => {
        setEditingSession(null); // Ensure we are creating a NEW session, not updating
        setScheduledDate(formatDate(new Date())); // Default to today's date for the new session
        setAdminNotes(session.adminNotes || ''); // Copy notes
        setSelectedMetrics(new Set(session.metricsToTrain || [])); // Copy metrics
        setTargetPlayerId(selectedPlayer.userId); // Default to current player, but allow change
        setShowForm(true); // Open the form
        onShowMessage("Session details copied! Select a player (if different) and Save.");
    };

    const handleSaveSession = async (e) => {
        e.preventDefault();
        try { 
            const commonData = {
                scheduledDate, 
                metricsToTrain: Array.from(selectedMetrics), 
                adminNotes
            };

            if (editingSession) {
                // UPDATE EXISTING SESSION (Must be for currently selected player)
                const sessionDocRef = doc(db, `artifacts/${appId}/public/data/profiles/${selectedPlayer.userId}/training_sessions`, editingSession.id);
                await updateDoc(sessionDocRef, commonData);
                onShowMessage(`Session updated successfully.`);
            } else {
                // CREATE NEW SESSION (Use targetPlayerId from dropdown)
                await addDoc(collection(db, `artifacts/${appId}/public/data/profiles/${targetPlayerId}/training_sessions`), { 
                    playerId: targetPlayerId, 
                    ...commonData, 
                    status: "Pending", 
                    linkedReportId: null 
                }); 
                
                const targetName = activePlayers.find(p => p.userId === targetPlayerId)?.firstName || 'Player';
                onShowMessage(`Session scheduled successfully for ${targetName}.`);
            }
            resetForm();
        } catch(e) { 
            onShowMessage(e.message); 
        }
    };

    const toggleMetric = (id) => { const s = new Set(selectedMetrics); if(s.has(id)) s.delete(id); else s.add(id); setSelectedMetrics(s); };

    // Helper to organize metrics by category
    const getGroupedSessionMetrics = (metricIds) => {
        const groups = {};
        (metricIds || []).forEach(id => {
            const metric = allMetrics.find(m => m.id === id);
            if (metric) {
                const cat = metric.category || 'Other';
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push(metric.name);
            }
        });
        return groups;
    };

    return (
        <>
            <ConfirmationModal isOpen={!!sessionToDelete} message="Delete this session?" onConfirm={confirmDelete} onCancel={() => setSessionToDelete(null)} />
            <div className="space-y-6">
                <h4 className="text-2xl font-semibold text-indigo-700">Training Planner</h4>
                <div className="flex flex-col space-y-2">
                    <label className="text-gray-700 font-medium">Select Player to View/Edit Plans:</label>
                    <select onChange={e => setSelectedPlayer(activePlayers.find(p=>p.userId===e.target.value)||null)} className="w-full p-3 border rounded"><option value="">-- Choose Player --</option>{activePlayers.map(p=><option key={p.userId} value={p.userId}>{p.firstName} {p.lastName} ({p.email})</option>)}</select>
                </div>

                {selectedPlayer && (
                    <div className="pt-6 border-t space-y-6">
                        <button onClick={() => { if(showForm) resetForm(); else setShowForm(true); }} className={`py-2 px-4 text-white rounded ${showForm ? 'bg-red-500' : 'bg-orange-500'}`}>
                            {showForm ? 'Cancel' : 'Schedule New Session'}
                        </button>
                        
                        {showForm && (
                            <form onSubmit={handleSaveSession} className="p-6 bg-gray-50 border rounded space-y-4">
                                <h6 className="font-bold text-gray-700">{editingSession ? 'Edit Session' : 'New Session Details'}</h6>
                                
                                {/* NEW: Target Player Selection (Only when creating/duplicating) */}
                                {!editingSession && (
                                    <div className="p-3 bg-blue-50 border border-blue-100 rounded">
                                        <label className="block text-sm font-bold text-blue-800 mb-1">Assign To Player:</label>
                                        <select 
                                            value={targetPlayerId} 
                                            onChange={(e) => setTargetPlayerId(e.target.value)}
                                            className="w-full p-2 border border-blue-300 rounded"
                                        >
                                            {activePlayers.map(p => (
                                                <option key={p.userId} value={p.userId}>
                                                    {p.firstName} {p.lastName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <input type="date" value={scheduledDate} onChange={e=>setScheduledDate(e.target.value)} className="w-full p-2 border rounded" required />
                                <textarea value={adminNotes} onChange={e=>setAdminNotes(e.target.value)} placeholder="Notes for coach" className="w-full p-2 border rounded" />
                                <div className="grid grid-cols-2 gap-2">{allMetrics.map(m=><label key={m.id} className="flex items-center space-x-2"><input type="checkbox" checked={selectedMetrics.has(m.id)} onChange={()=>toggleMetric(m.id)} /><span>{m.name}</span></label>)}</div>
                                <button className="w-full py-2 bg-indigo-600 text-white rounded">{editingSession ? 'Update Session' : 'Save Session'}</button>
                            </form>
                        )}

                        <div className="space-y-4">
                            {sessions.map(s => {
                                // Updated Logic: Allow editing for Today's sessions as well (>= instead of >)
                                const isFutureDated = s.scheduledDate >= todayStr;
                                const isPending = s.status === 'Pending';
                                const groupedMetrics = getGroupedSessionMetrics(s.metricsToTrain);

                                return (
                                    <div key={s.id} className="p-4 border rounded bg-white shadow-sm">
                                        <div className="flex justify-between font-bold">
                                            <span>{s.scheduledDate}</span>
                                            <span className={`text-xs px-2 py-1 rounded ${s.status === 'Completed' ? 'bg-green-200' : 'bg-gray-200'}`}>{s.status}</span>
                                        </div>
                                        <p className="text-sm mt-1 text-gray-700">Notes: {s.adminNotes || 'N/A'}</p>
                                         
                                        {/* Grouped Metrics Display */}
                                        <div className="mt-3">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">Focus Areas</span>
                                            {Object.keys(groupedMetrics).length === 0 ? (
                                                <p className="text-xs text-gray-400 italic">No specific metrics assigned.</p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {Object.keys(groupedMetrics).sort().map(category => (
                                                        <div key={category} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                                            <span className="text-xs font-bold text-gray-600 min-w-[80px] pt-0.5">{category}:</span>
                                                            <div className="flex flex-wrap gap-1">
                                                                {groupedMetrics[category].map(mName => (
                                                                    <span key={mName} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100">
                                                                        {mName}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                         
                                        {/* Actions */}
                                        <div className="mt-4 flex space-x-3 pt-3 border-t border-gray-100">
                                            <button 
                                                onClick={() => handleDuplicateSession(s)}
                                                className="text-xs py-1 px-3 bg-purple-50 hover:bg-purple-100 text-purple-700 font-semibold rounded transition"
                                            >
                                                Duplicate
                                            </button>

                                            {(isFutureDated && isPending) && (
                                                <>
                                                    <button 
                                                        onClick={() => handleEditSession(s)}
                                                        className="text-xs py-1 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded transition"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button 
                                                        onClick={() => setSessionToDelete({...s, playerId: selectedPlayer.userId})} 
                                                        className="text-xs py-1 px-3 bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded transition"
                                                    >
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

const CoachProgressHistory = ({ db, user, profile, allUsers, allMetrics, onShowMessage }) => {
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [showGeneralReportForm, setShowGeneralReportForm] = useState(false);
    const currentUserId = user.uid;
    const coachRoster = useMemo(() => allUsers.filter(player => player.isActive && safeRoles(player).includes('Player') && (player.assignedCoaches||[]).includes(currentUserId)).sort((a,b)=>(a.lastName||'').localeCompare(b.lastName||'')), [allUsers, currentUserId]);
     
    return (
        <div className="space-y-6">
            <h4 className="text-2xl font-semibold text-gray-800">Player Progress</h4>
            <select onChange={e => setSelectedPlayer(coachRoster.find(p=>p.userId===e.target.value)||null)} className="w-full p-2 border rounded"><option value="">Select Player</option>{coachRoster.map(p=><option key={p.userId} value={p.userId}>{p.firstName} {p.lastName}</option>)}</select>
            {selectedPlayer && (
                <div className="pt-4 border-t">
                    {showGeneralReportForm && <NewProgressReportForm db={db} player={selectedPlayer} coach={{uid:user.uid, profile}} allMetrics={allMetrics} onShowMessage={onShowMessage} onClose={()=>setShowGeneralReportForm(false)} />}
                    <PlayerProgressViewer db={db} onShowMessage={onShowMessage} player={selectedPlayer} coach={{uid:user.uid, profile}} allMetrics={allMetrics} isReadOnly={false} onShowGeneralReportForm={()=>setShowGeneralReportForm(true)} />
                </div>
            )}
        </div>
    );
};

const CoachTrainingPlanner = ({ db, user, profile, allUsers, allMetrics, onShowMessage }) => {
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [showPlannedReportForm, setShowPlannedReportForm] = useState(false);
    const [activeSession, setActiveSession] = useState(null); 
    
    // NEW: Video Modal State
    const [videoModalSession, setVideoModalSession] = useState(null);
    const [videoList, setVideoList] = useState([]); // List of video URLs
    const [newVideoUrl, setNewVideoUrl] = useState(''); // Input for new URL

    const currentUserId = user.uid;
    const appId = STATIC_APP_ID;
    const todayStr = formatDate(new Date());
    const coachRoster = useMemo(() => allUsers.filter(player => player.isActive && safeRoles(player).includes('Player') && (player.assignedCoaches||[]).includes(currentUserId)).sort((a,b)=>(a.lastName||'').localeCompare(b.lastName||'')), [allUsers, currentUserId]);

    useEffect(() => {
        if (!db || !selectedPlayer) { setSessions([]); return; }
        const q = query(collection(db, `artifacts/${appId}/public/data/profiles/${selectedPlayer.userId}/training_sessions`), orderBy('scheduledDate', 'desc'));
        return onSnapshot(q, (snap) => { const list=[]; snap.forEach(d=>list.push({id:d.id, ...d.data()})); setSessions(list); });
    }, [db, selectedPlayer, appId]);

    // Helper to organize metrics by category
    const getGroupedSessionMetrics = (metricIds) => {
        const groups = {};
        (metricIds || []).forEach(id => {
            const metric = allMetrics.find(m => m.id === id);
            if (metric) {
                const cat = metric.category || 'Other';
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push(metric.name);
            }
        });
        return groups;
    };

    const handleCloseForm = () => {
        setShowPlannedReportForm(false);
        setActiveSession(null);
    };

    // NEW: Video Handlers
    const handleOpenVideoModal = (session) => {
        setVideoModalSession(session);
        // Load existing videos or fallback to legacy single url
        const existing = session.sessionVideos || (session.sessionVideoUrl ? [session.sessionVideoUrl] : []);
        setVideoList(existing);
        setNewVideoUrl('');
    };

    const handleAddVideoToList = () => {
        if (!newVideoUrl) return;
        setVideoList([...videoList, newVideoUrl]);
        setNewVideoUrl('');
    };

    const handleRemoveVideoFromList = (index) => {
        const newList = [...videoList];
        newList.splice(index, 1);
        setVideoList(newList);
    };

    const handleSaveVideos = async () => {
        if (!videoModalSession) return;
        try {
            const sessionDocRef = doc(db, `artifacts/${appId}/public/data/profiles/${selectedPlayer.userId}/training_sessions`, videoModalSession.id);
            await updateDoc(sessionDocRef, { 
                sessionVideos: videoList,
                sessionVideoUrl: videoList.length > 0 ? videoList[0] : "" // Keep legacy sync
            });
            onShowMessage("Videos updated successfully!");
            setVideoModalSession(null);
        } catch (error) {
            console.error("Error saving videos:", error);
            onShowMessage(`Error: ${error.message}`);
        }
    };

    return (
        <div className="space-y-6">
            <h4 className="text-2xl font-semibold text-gray-800">Training Agenda</h4>
            <select onChange={e => setSelectedPlayer(coachRoster.find(p=>p.userId===e.target.value)||null)} className="w-full p-2 border rounded"><option value="">Select Player</option>{coachRoster.map(p=><option key={p.userId} value={p.userId}>{p.firstName} {p.lastName}</option>)}</select>
            {selectedPlayer && (
                <div className="pt-4 border-t space-y-4">
                    
                    {/* VIDEO MODAL */}
                    {videoModalSession && (
                        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex items-center justify-center p-4">
                            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
                                <h3 className="text-lg font-bold text-gray-800">Manage Session Videos</h3>
                                <p className="text-sm text-gray-600">Add or remove YouTube links for <b>{videoModalSession.scheduledDate}</b>.</p>
                                
                                {/* List of current videos */}
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {videoList.map((url, idx) => (
                                        <div key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded border">
                                            <span className="text-xs text-gray-600 truncate w-64">{url}</span>
                                            <button onClick={() => handleRemoveVideoFromList(idx)} className="text-red-500 hover:text-red-700 font-bold px-2">X</button>
                                        </div>
                                    ))}
                                    {videoList.length === 0 && <p className="text-xs text-gray-400 italic">No videos attached yet.</p>}
                                </div>

                                <div className="flex space-x-2">
                                    <input 
                                        type="text" 
                                        placeholder="Paste YouTube link here..." 
                                        value={newVideoUrl} 
                                        onChange={(e) => setNewVideoUrl(e.target.value)} 
                                        className="w-full p-2 border rounded text-sm"
                                    />
                                    <button onClick={handleAddVideoToList} className="bg-indigo-100 text-indigo-700 px-3 rounded hover:bg-indigo-200">Add</button>
                                </div>

                                <div className="flex justify-end space-x-2 pt-4 border-t">
                                    <button onClick={() => setVideoModalSession(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                                    <button onClick={handleSaveVideos} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save Changes</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showPlannedReportForm && activeSession && (
                        <NewProgressReportForm 
                            db={db} 
                            player={selectedPlayer} 
                            coach={{uid:user.uid, profile}} 
                            allMetrics={allMetrics} 
                            onShowMessage={onShowMessage} 
                            onClose={handleCloseForm} 
                            plannedMetrics={activeSession.metricsToTrain} 
                            plannedNotes={activeSession.adminNotes} 
                            sessionToUpdateId={activeSession.id} 
                        />
                    )}

                    {(() => {
                        const todaysSession = sessions.find(s => s.scheduledDate === todayStr && s.status === 'Pending');
                        if (todaysSession && !showPlannedReportForm) {
                            return (
                                <button 
                                    onClick={() => { setActiveSession(todaysSession); setShowPlannedReportForm(true); }} 
                                    className="w-full py-3 bg-blue-600 text-white rounded font-bold shadow-lg animate-pulse mb-4"
                                >
                                    Start Today's Planned Session ({todaysSession.scheduledDate})
                                </button>
                            );
                        }
                        return null;
                    })()}

                    {sessions.map(s => {
                        const groupedMetrics = getGroupedSessionMetrics(s.metricsToTrain);
                        const isPending = s.status === 'Pending';
                        const hasPlayerResponse = s.playerAssessmentSubmitted === true;
                        // Count videos for display
                        const videoCount = (s.sessionVideos?.length) || (s.sessionVideoUrl ? 1 : 0);
                        
                        return (
                            <div key={s.id} className={`p-4 border rounded bg-white shadow-sm ${s.id === activeSession?.id ? 'ring-2 ring-blue-500' : ''}`}>
                                <div className="flex justify-between font-bold items-center">
                                    <span>{s.scheduledDate}</span>
                                    <div className="flex items-center space-x-2">
                                        {hasPlayerResponse && (
                                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded border border-purple-200">
                                                Self-Assessed
                                            </span>
                                        )}
                                        <span className={`text-xs px-2 py-1 rounded ${s.status==='Completed'?'bg-green-200':'bg-gray-200'}`}>{s.status}</span>
                                    </div>
                                </div>
                                <p className="text-sm mt-1 text-gray-600">{s.adminNotes || "No notes provided."}</p>
                                 
                                <div className="mt-3">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">Focus Areas</span>
                                    {Object.keys(groupedMetrics).length === 0 ? (
                                        <p className="text-xs text-gray-400 italic">No specific metrics assigned.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {Object.keys(groupedMetrics).sort().map(category => (
                                                <div key={category} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                                    <span className="text-xs font-bold text-gray-600 min-w-[80px] pt-0.5">{category}:</span>
                                                    <div className="flex flex-wrap gap-1">
                                                        {groupedMetrics[category].map(mName => (
                                                            <span key={mName} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100">
                                                                {mName}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 pt-3 border-t flex justify-between items-center">
                                    {/* VIDEO BUTTON */}
                                    <button 
                                        onClick={() => handleOpenVideoModal(s)}
                                        className={`flex items-center space-x-1 text-sm font-medium px-3 py-1.5 rounded transition ${videoCount > 0 ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
                                        <span>{videoCount > 0 ? `Manage Videos (${videoCount})` : 'Add Video'}</span>
                                    </button>

                                    {isPending && !showPlannedReportForm && (
                                        <button 
                                            onClick={() => { setActiveSession(s); setShowPlannedReportForm(true); }}
                                            className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold rounded transition text-sm"
                                        >
                                            File Report
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PlayerTrainingPlan = ({ db, onShowMessage, player, allMetrics, onViewProgress }) => {
    const appId = STATIC_APP_ID;
    const [sessions, setSessions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [showAssessmentForm, setShowAssessmentForm] = useState(false);

    useEffect(() => {
        if(!db || !player) return;
        const q = query(collection(db, `artifacts/${appId}/public/data/profiles/${player.userId}/training_sessions`), orderBy('scheduledDate', 'desc'));
        return onSnapshot(q, (snap) => { const list=[]; snap.forEach(d=>list.push({id:d.id, ...d.data()})); setSessions(list); });
    }, [db, player, appId]);

    const getGroupedSessionMetrics = (metricIds) => {
        const groups = {};
        (metricIds || []).forEach(id => {
            const metric = allMetrics.find(m => m.id === id);
            if (metric) {
                const cat = metric.category || 'Other';
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push(metric.name);
            }
        });
        return groups;
    };

    const handleCloseForm = () => {
        setShowAssessmentForm(false);
        setActiveSession(null);
    };

    return (
        <div className="space-y-6">
            <h4 className="text-2xl font-bold text-gray-800">My Training Plan</h4>
            
            {showAssessmentForm && activeSession && (
                <div className="mb-6">
                    <PlayerSelfAssessmentForm 
                        db={db} 
                        player={player} 
                        allMetrics={allMetrics} 
                        onShowMessage={onShowMessage} 
                        onClose={handleCloseForm}
                        sessionToUpdateId={activeSession.id}
                        plannedMetrics={activeSession.metricsToTrain}
                    />
                </div>
            )}

            {sessions.length===0 ? <p className="text-gray-500">No sessions planned.</p> : sessions.map(s => {
                const groupedMetrics = getGroupedSessionMetrics(s.metricsToTrain);
                const hasAssessment = s.playerAssessmentSubmitted === true;
                
                // Normalise videos
                const displayVideos = s.sessionVideos || (s.sessionVideoUrl ? [s.sessionVideoUrl] : []);

                return (
                    <div key={s.id} className={`p-4 rounded-lg border shadow-sm ${s.status === 'Completed' ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'} ${activeSession?.id === s.id ? 'ring-2 ring-blue-400' : ''}`}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-lg font-bold text-indigo-700">{s.scheduledDate}</span>
                            <div className="flex space-x-2">
                                {hasAssessment && <span className="text-xs font-bold bg-blue-100 text-blue-800 py-1 px-2 rounded-full">Self Assessed</span>}
                                <span className={`text-xs font-semibold py-1 px-3 rounded-full ${s.status === 'Completed' ? 'bg-green-200 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{s.status}</span>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2"><span className="font-semibold">Coach Notes:</span> {s.adminNotes || 'N/A'}</p>
                         
                        <div className="mt-3">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">Focus Areas</span>
                            {Object.keys(groupedMetrics).length === 0 ? (
                                <p className="text-xs text-gray-400 italic">No specific metrics assigned.</p>
                            ) : (
                                <div className="space-y-2">
                                    {Object.keys(groupedMetrics).sort().map(category => (
                                        <div key={category} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                            <span className="text-xs font-bold text-gray-600 min-w-[80px] pt-0.5">{category}:</span>
                                            <div className="flex flex-wrap gap-1">
                                                {groupedMetrics[category].map(mName => (
                                                    <span key={mName} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100">
                                                        {mName}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-4 pt-3 border-t flex flex-col md:flex-row justify-end items-end md:items-center space-y-2 md:space-y-0 md:space-x-3">
                            {/* WATCH VIDEO BUTTONS */}
                            {displayVideos.length > 0 && (
                                <div className="flex flex-wrap gap-2 justify-end">
                                    {displayVideos.map((url, i) => (
                                        <a 
                                            key={i}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded shadow transition"
                                        >
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                            <span>Watch Video {displayVideos.length > 1 ? i + 1 : ''}</span>
                                        </a>
                                    ))}
                                </div>
                            )}

                            {!hasAssessment ? (
                                <button 
                                    onClick={() => { setActiveSession(s); setShowAssessmentForm(true); }}
                                    className="py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded shadow transition"
                                >
                                    Start Self-Assessment
                                </button>
                            ) : (
                                <button 
                                    onClick={onViewProgress}
                                    className="text-sm text-indigo-600 hover:text-indigo-800 underline font-medium"
                                >
                                    View Assessment
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// --- Dashboards ---

const AdminDashboard = ({ db, user, profile, onShowMessage, allUsers, allMetrics, onLogout, roleSwitcherComponent, onShowProfile }) => {
    const [activeTab, setActiveTab] = useState('announcements'); 
    const TABS = [
        { id: 'announcements', label: 'Announcements' },
        { id: 'roles', label: 'User Roles' },
        { id: 'relationships', label: 'Relationships' },
        { id: 'planner', label: 'Training Planner' },
        { id: 'metrics', label: 'Metrics Library' },
        { id: 'resources', label: 'Drills & Resources' }, // NEW TAB
        { id: 'attendance', label: 'Attendance' }
    ];

    const renderTabContent = () => {
        switch(activeTab) {
            case 'announcements': return <AdminAnnouncements db={db} profile={profile} allUsers={allUsers} onShowMessage={onShowMessage} />;
            case 'roles': return <UserManagementList db={db} currentUserId={user.uid} onShowMessage={onShowMessage} allUsers={allUsers} />;
            case 'relationships': return <UserRelationshipManager db={db} allUsers={allUsers} onShowMessage={onShowMessage} />;
            case 'planner': return <AdminTrainingPlanner db={db} allUsers={allUsers} allMetrics={allMetrics} onShowMessage={onShowMessage} />;
            case 'metrics': return <AdminMetricsManager db={db} onShowMessage={onShowMessage} allMetrics={allMetrics} />;
            case 'resources': return <ResourceLibrary db={db} user={user} role="Admin" onShowMessage={onShowMessage} allUsers={allUsers} />;
            case 'attendance': return <AttendanceTracker db={db} roster={allUsers.filter(u=>u.isActive && safeRoles(u).includes('Player'))} currentUserId={user.uid} onShowMessage={onShowMessage} />;
            default: return null;
        }
    };

    return (
        <>
            <div className="w-full max-w-4xl bg-white p-4 shadow-md rounded-t-xl flex justify-between items-center">
                <div className="flex items-center"><AcademyLogoHeader /><h3 className="text-2xl font-bold text-indigo-700">Premier Cricket Academy</h3></div>
                <DashboardNavMenu onLogout={onLogout} roleSwitcherComponent={roleSwitcherComponent} onShowProfile={onShowProfile} tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
            </div>
            <div className="w-full max-w-4xl p-4 bg-gray-50 rounded-b-xl shadow-inner">{renderTabContent()}</div>
        </>
    );
};

const CoachDashboard = ({ db, user, profile, allUsers, allMetrics, onShowMessage, onLogout, roleSwitcherComponent, onShowProfile }) => {
    const [activeTab, setActiveTab] = useState('announcements'); 
    const currentUserId = user.uid;
    const TABS = [
        { id: 'announcements', label: 'Announcements' },
        { id: 'planner', label: 'Training Planner' },
        { id: 'history', label: 'Player Progress' },
        { id: 'resources', label: 'Drills & Resources' }, // NEW TAB
        { id: 'roster', label: 'Assigned Players' },
        { id: 'attendance', label: 'Mark Attendance' }
    ];

    const coachRoster = useMemo(() => {
        return allUsers.filter(player => {
            const isPlayer = player.isActive && safeRoles(player).includes('Player');
            return isPlayer && (player.assignedCoaches || []).includes(currentUserId);
        }).sort((a, b) => (a.lastName||'').localeCompare(b.lastName||''));
    }, [allUsers, currentUserId]);

    const renderTabContent = () => {
        switch(activeTab) {
            case 'announcements': return <UserAnnouncements db={db} profile={profile} onShowMessage={onShowMessage} />;
            case 'roster': return (
                <div className="space-y-4">
                    <h4 className="text-2xl font-bold text-gray-800">My Players</h4>
                    {coachRoster.map(p => <div key={p.userId} className="p-4 bg-white border rounded shadow-sm flex justify-between"><span>{p.firstName} {p.lastName}</span><span className="text-gray-500 text-sm">{p.email}</span></div>)}
                </div>
            );
            case 'attendance': return <AttendanceTracker db={db} roster={coachRoster} currentUserId={currentUserId} onShowMessage={onShowMessage} />;
            case 'planner': return <CoachTrainingPlanner db={db} user={user} profile={profile} allUsers={allUsers} allMetrics={allMetrics} onShowMessage={onShowMessage} />;
            case 'history': return <CoachProgressHistory db={db} user={user} profile={profile} allUsers={allUsers} allMetrics={allMetrics} onShowMessage={onShowMessage} />;
            case 'resources': return <ResourceLibrary db={db} user={user} role="Coach" onShowMessage={onShowMessage} allUsers={allUsers} />;
            default: return null;
        }
    };

    return (
        <>
            <div className="w-full max-w-4xl bg-white p-4 shadow-md rounded-t-xl flex justify-between items-center">
                <div className="flex items-center"><AcademyLogoHeader /><h3 className="text-2xl font-bold text-green-700">Coach Dashboard</h3></div>
                <DashboardNavMenu onLogout={onLogout} roleSwitcherComponent={roleSwitcherComponent} onShowProfile={onShowProfile} tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
            </div>
            <div className="w-full max-w-4xl p-4 bg-gray-50 rounded-b-xl shadow-inner">{renderTabContent()}</div>
        </>
    );
};

const PlayerDashboard = ({ db, user, profile, onShowMessage, allMetrics, onLogout, roleSwitcherComponent, onShowProfile }) => {
    const [activeTab, setActiveTab] = useState('announcements'); 
    const [showSelfAssessmentForm, setShowSelfAssessmentForm] = useState(false);
    const TABS = [
        { id: 'announcements', label: 'Announcements' },
        { id: 'progress', label: 'My Progress' },
        { id: 'plan', label: 'Training Plan' },
        { id: 'resources', label: 'Drills & Resources' } // NEW TAB
    ];

    const renderTabContent = () => {
        switch(activeTab) {
            case 'announcements': return <UserAnnouncements db={db} profile={profile} onShowMessage={onShowMessage} />;
            case 'progress': return (
                <div className="space-y-6">
                    {showSelfAssessmentForm ? <PlayerSelfAssessmentForm db={db} player={profile} allMetrics={allMetrics} onShowMessage={onShowMessage} onClose={() => setShowSelfAssessmentForm(false)} /> : <button onClick={() => setShowSelfAssessmentForm(true)} className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded shadow">+ Add Self-Assessment</button>}
                    <PlayerProgressViewer db={db} onShowMessage={onShowMessage} player={profile} coach={null} allMetrics={allMetrics} isReadOnly={true} />
                </div>
            );
            case 'plan': return <PlayerTrainingPlan db={db} onShowMessage={onShowMessage} player={profile} allMetrics={allMetrics} onViewProgress={() => setActiveTab('progress')} />;
            case 'resources': return <ResourceLibrary db={db} user={user} role="Player" onShowMessage={onShowMessage} />;
            default: return null;
        }
    };

    return (
        <>
            <div className="w-full max-w-4xl bg-white p-4 shadow-md rounded-t-xl flex justify-between items-center">
                <div className="flex items-center"><AcademyLogoHeader /><h3 className="text-2xl font-bold text-blue-700">Player Dashboard</h3></div>
                <DashboardNavMenu onLogout={onLogout} roleSwitcherComponent={roleSwitcherComponent} onShowProfile={onShowProfile} tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
            </div>
            <div className="w-full max-w-4xl p-4 bg-gray-50 rounded-b-xl shadow-inner">{renderTabContent()}</div>
        </>
    );
};

const ParentDashboard = ({ db, user, profile, allUsers, allMetrics, onShowMessage, onLogout, roleSwitcherComponent, onShowProfile }) => {
    const [activeTab, setActiveTab] = useState('announcements'); 
    const [selectedChild, setSelectedChild] = useState(null);
    const currentUserId = user.uid;
    const assignedChildren = useMemo(() => allUsers.filter(player => player.isActive && safeRoles(player).includes('Player') && (player.assignedParents||[]).includes(currentUserId)), [allUsers, currentUserId]);
    const TABS = [{ id: 'announcements', label: 'Announcements' }, { id: 'childview', label: 'Player Progress' }, { id: 'resources', label: 'Drills & Resources' }];

    const renderTabContent = () => {
        if (activeTab === 'announcements') return <UserAnnouncements db={db} profile={profile} onShowMessage={onShowMessage} />;
        if (activeTab === 'resources') return <ResourceLibrary db={db} user={user} role="Parent" onShowMessage={onShowMessage} allUsers={allUsers} />;
        if (activeTab === 'childview') {
            if (selectedChild) return <><button onClick={() => setSelectedChild(null)} className="mb-4 text-indigo-600 font-medium">&larr; Back to Children</button><PlayerProgressViewer db={db} onShowMessage={onShowMessage} player={selectedChild} coach={null} allMetrics={allMetrics} isReadOnly={true} /></>;
            return (
                <div className="space-y-4">
                    <h4 className="text-2xl font-bold text-gray-800">My Children</h4>
                    {assignedChildren.length===0?<p>No active players assigned.</p> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{assignedChildren.map(c => <button key={c.userId} onClick={()=>setSelectedChild(c)} className="p-4 bg-white border rounded shadow hover:shadow-md text-left"><h5 className="font-bold text-indigo-600">{c.firstName} {c.lastName}</h5></button>)}</div>}
                </div>
            );
        }
    };

    return (
        <>
            <div className="w-full max-w-4xl bg-white p-4 shadow-md rounded-t-xl flex justify-between items-center">
                <div className="flex items-center"><AcademyLogoHeader /><h3 className="text-2xl font-bold text-purple-700">Parent Dashboard</h3></div>
                <DashboardNavMenu onLogout={onLogout} roleSwitcherComponent={roleSwitcherComponent} onShowProfile={onShowProfile} tabs={TABS} activeTab={activeTab} onTabChange={(t)=>{setActiveTab(t); if(t!=='childview') setSelectedChild(null);}} />
            </div>
            <div className="w-full max-w-4xl p-4 bg-gray-50 rounded-b-xl shadow-inner">{renderTabContent()}</div>
        </>
    );
};

// --- Auth & Main ---

const RegisterCard = ({ auth, db, navigate, onShowMessage }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const userId = userCredential.user.uid;
      const appId = STATIC_APP_ID;
      const userDocRef = doc(db, `artifacts/${appId}/public/data/profiles`, userId);
      await setDoc(userDocRef, { userId, roles: ["Player"], isActive: true, email, firstName, lastName, createdAt: new Date().toISOString() });
    } catch (error) {
      onShowMessage(`Registration Failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

    return (
        <div className="w-full max-w-md bg-white p-8 md:p-10 rounded-xl shadow-2xl transition duration-300">
            <div className="flex justify-center mb-6"><img src={ACADEMY_LOGO_URL} alt="Logo" className="w-32 h-32 rounded-full shadow-lg border-2 border-orange-500 p-1" onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/128x128/0A2342/FFFFFF?text=Logo"; }} /></div>
            <h2 className="text-3xl font-bold text-center text-indigo-700 mb-2">Create Account</h2>
            <form onSubmit={handleRegister} className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
                    <input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                    <input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                </div>
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                <input type="password" placeholder="Password (min 6 chars)" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                <button type="submit" disabled={isLoading} className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white text-lg font-semibold rounded-lg shadow-md disabled:opacity-50">{isLoading ? 'Registering...' : 'Sign Up'}</button>
            </form>
            <p className="mt-6 text-center text-sm text-gray-600">Already have an account? <button onClick={() => navigate('login')} className="ml-1 text-orange-500 font-semibold">Sign In</button></p>
        </div>
    );
};

const LoginCard = ({ auth, navigate, onShowMessage }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try { await signInWithEmailAndPassword(auth, email, password); } catch (error) { onShowMessage(`Login Failed: ${error.message}`); } finally { setIsLoading(false); }
  };

  return (
    <>
        <ForgotPasswordModal isOpen={isForgotPasswordModalOpen} onClose={() => setIsForgotPasswordModalOpen(false)} auth={auth} onShowMessage={onShowMessage} />
        <div className="w-full max-w-md bg-white p-8 md:p-10 rounded-xl shadow-2xl transition duration-300">
            <div className="flex justify-center mb-6"><img src={ACADEMY_LOGO_URL} alt="Logo" className="w-32 h-32 rounded-full shadow-lg border-2 border-orange-500 p-1" onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/128x128/0A2342/FFFFFF?text=Logo"; }} /></div>
            <h2 className="text-3xl font-bold text-center text-indigo-700 mb-2">Welcome Back</h2>
            <form onSubmit={handleLogin} className="space-y-4">
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                <div className="text-right"><button type="button" onClick={() => setIsForgotPasswordModalOpen(true)} className="text-sm text-indigo-600 font-medium">Forgot Password?</button></div>
                <button type="submit" disabled={isLoading} className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white text-lg font-semibold rounded-lg shadow-md disabled:opacity-50">{isLoading ? 'Signing In...' : 'Login'}</button>
            </form>
            <p className="mt-6 text-center text-sm text-gray-600">Don't have an account? <button onClick={() => navigate('register')} className="ml-1 text-orange-500 font-semibold">Sign Up</button></p>
        </div>
    </>
  );
};

const DashboardContent = ({ auth, user, profile, isProfileLoading, navigate, db, onShowMessage }) => {
    const [fetchedUsers, setFetchedUsers] = useState([]);
    const [areAllProfilesLoading, setAreAllProfilesLoading] = useState(true);
    const [fetchedMetrics, setFetchedMetrics] = useState([]);
    const [areMetricsLoading, setAreMetricsLoading] = useState(true);
    const [sortedRoles, setSortedRoles] = useState([]);
    const [activeRole, setActiveRole] = useState(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  useEffect(() => {
    if (!db || isProfileLoading || !profile) return;
    const userRoles = profile.roles || [];
    if (!userRoles.some(r => ['Admin', 'Coach', 'Parent'].includes(r))) { setAreAllProfilesLoading(false); return; }
    const q = query(collection(db, `artifacts/${STATIC_APP_ID}/public/data/profiles`));
    return onSnapshot(q, (snapshot) => {
        const profiles = []; snapshot.forEach(doc => profiles.push(doc.data()));
        setFetchedUsers(profiles); setAreAllProfilesLoading(false);
    });
  }, [db, profile, isProfileLoading]);
     
    useEffect(() => {
        if (!db || isProfileLoading || !profile) return;
        const q = query(collection(db, `artifacts/${STATIC_APP_ID}/public/data/metrics`));
        return onSnapshot(q, (snapshot) => {
            const metrics = []; snapshot.forEach(doc => metrics.push({ id: doc.id, ...doc.data() }));
            setFetchedMetrics(metrics); setAreMetricsLoading(false);
        });
    }, [db, profile, isProfileLoading]);

    useEffect(() => {
        if (profile) {
            const userRoles = safeRoles(profile);
            const newSortedRoles = ROLE_PRIORITY.filter(role => userRoles.includes(role));
            setSortedRoles(newSortedRoles);
            if (newSortedRoles.length > 0) setActiveRole(newSortedRoles[0]);
        }
    }, [profile]);
     
  const handleLogout = async () => { try { await signOut(auth); navigate('login'); } catch (error) { console.error(error); } };
  
  const RoleSwitcher = ({ sortedRoles, activeRole, onRoleChange }) => {
    if (sortedRoles.length <= 1) return <div className="px-4 py-2"><span className="block text-sm text-gray-500 mb-1">Viewing as:</span><span className="font-medium text-gray-800">{activeRole || 'No Role'}</span></div>;
    return <div className="px-4 py-2"><label className="block text-sm text-gray-500 mb-1">Switch View:</label><select value={activeRole} onChange={(e) => onRoleChange(e.target.value)} className="w-full p-2 border rounded-lg shadow-sm">{sortedRoles.map(role => <option key={role} value={role}>{role} Dashboard</option>)}</select></div>;
  };

  const renderRoleDashboard = () => {
    if (isProfileLoading) return <div className="text-center text-xl text-indigo-500 py-10">Loading User Profile...</div>;
    if (!profile) return <div className="text-center text-xl text-red-500 py-10">Error: Could not load user profile.</div>;
    if (profile.isActive === false) return <DisabledAccountCard auth={auth} navigate={navigate} />;
     
    const needsAllProfiles = sortedRoles.some(r => ['Admin', 'Coach', 'Parent'].includes(r));
    if (areMetricsLoading || (needsAllProfiles && areAllProfilesLoading)) return <div className="text-center text-xl text-indigo-500 py-10">Loading Data...</div>;
     
    const props = { db, user, profile, onShowMessage, allUsers: fetchedUsers, allMetrics: fetchedMetrics, onLogout: handleLogout, roleSwitcherComponent: <RoleSwitcher sortedRoles={sortedRoles} activeRole={activeRole} onRoleChange={setActiveRole} />, onShowProfile: () => setIsProfileModalOpen(true) };

    switch(activeRole) {
        case 'Admin': return <AdminDashboard {...props} />;
        case 'Coach': return <CoachDashboard {...props} />;
        case 'Parent': return <ParentDashboard {...props} />;
        case 'Player': return <PlayerDashboard {...props} />;
        default: return <div className="text-center text-xl text-gray-500 py-10">Welcome! Your profile has no assigned role.</div>;
    }
  };
     
  return (
    <div className="w-full max-w-4xl">
      {renderRoleDashboard()}
      <MyProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} profile={profile} db={db} onShowMessage={onShowMessage} />
    </div>
  );
};

const App = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [authInstance, setAuthInstance] = useState(null);
  const [dbInstance, setDbInstance] = useState(null);
  const [currentPage, setCurrentPage] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const onShowMessage = useCallback((message) => { setErrorMessage(message); }, []);
  const navigate = useCallback((page) => { setCurrentPage(page); }, []);
  
  const fetchUserProfile = useCallback(async (db, userId) => {
    setIsProfileLoading(true);
    try {
        const docSnap = await getDoc(doc(db, `artifacts/${STATIC_APP_ID}/public/data/profiles`, userId));
        if (docSnap.exists()) setProfile(docSnap.data());
        else setProfile(null);
    } catch (error) { console.error(error); setProfile(null); } finally { setIsProfileLoading(false); }
  }, []);

  useEffect(() => {
    const setupFirebase = async () => {
      try {
        const app = initializeApp(firebaseConfig);
        const auth = getAuth(app);
        const db = getFirestore(app);
        setAuthInstance(auth); setDbInstance(db);
        
        const initialAuthToken = typeof window.__initial_auth_token !== 'undefined' ? window.__initial_auth_token : null;
        if (initialAuthToken) {
            try {
                await signInWithCustomToken(auth, initialAuthToken);
            } catch (e) {
                console.warn("Custom token auth failed, proceeding to manual login:", e.code);
            }
        }

        onAuthStateChanged(auth, (currentUser) => {
          setUser(currentUser);
          if (currentUser) { fetchUserProfile(db, currentUser.uid); setCurrentPage('dashboard'); }
          else if (authReady) setCurrentPage('login');
          if (!authReady) { setAuthReady(true); if (!currentUser) setCurrentPage('login'); }
        });
      } catch (error) { console.error(error); onShowMessage(`Firebase Init Failed: ${error.message}`); setAuthReady(true); setCurrentPage('login'); }
    };
    setupFirebase();
  }, [authReady, fetchUserProfile, onShowMessage]);

  const renderContent = () => {
    if (!authReady || currentPage === 'loading') return <div className="text-xl font-semibold text-indigo-500">Loading Application...</div>;
    if (!authInstance || !dbInstance) return <div className="text-xl font-semibold text-red-500 p-8">Error: Firebase not initialized.</div>;
    switch (currentPage) {
      case 'register': return <RegisterCard auth={authInstance} db={dbInstance} navigate={navigate} onShowMessage={onShowMessage} />;
      case 'dashboard': return user ? <DashboardContent auth={authInstance} user={user} profile={profile} isProfileLoading={isProfileLoading} navigate={navigate} db={dbInstance} onShowMessage={onShowMessage} /> : <LoginCard auth={authInstance} navigate={navigate} onShowMessage={onShowMessage} />;
      default: return <LoginCard auth={authInstance} navigate={navigate} onShowMessage={onShowMessage} />;
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <main className="w-full flex items-center justify-center">{renderContent()}</main>
      </div>
      <MessageBox message={errorMessage} onClose={() => setErrorMessage('')} />
    </>
  );
};

export default App;